import React, { useEffect, useState, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { toast } from 'sonner';
import { Camera, ExternalLink, AlertTriangle, ArrowUpDown } from 'lucide-react';
import { fmtRange } from '@/lib/dates';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HistoricalPhotosPendingView = ({ onOpenWorkOrder }) => {
  const { token } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState({ records: [], count: 0, customers: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ customer: '', branch: '', date: '' });
  const [sort, setSort] = useState('oldest');
  const [exc, setExc] = useState(null);      // record pending exception
  const [reason, setReason] = useState('');
  const [category, setCategory] = useState('both');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.customer) p.set('customer_id_name', filters.customer);
    p.set('sort', sort);
    axios.get(`${API}/work-orders/historical/photos-pending?sort=${sort}`, authHeaders)
      .then((r) => setData(r.data)).catch(() => toast.error('Could not load worklist')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [sort]);

  const rows = useMemo(() => data.records.filter((r) =>
    (!filters.customer || r.customer_name === filters.customer) &&
    (!filters.branch || (r.service_location || '').toLowerCase().includes(filters.branch.toLowerCase())) &&
    (!filters.date || r.service_date === filters.date)), [data, filters]);

  const submitException = async () => {
    if (reason.trim().length < 3) { toast.error('Please enter a reason'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/work-orders/historical/${exc.id}/photo-exception`, { reason: reason.trim(), category }, authHeaders);
      toast.success(`Exception submitted for approval — ${exc.tfs_work_order_number}`);
      setExc(null); setReason(''); setCategory('both'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not submit exception'); } finally { setBusy(false); }
  };

  return (
    <div data-testid="historical-photos-view">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">ROUT · Documentation</div>
      <h1 className="text-3xl font-extrabold text-[#0E2A47]">Historical Photos Pending</h1>
      <p className="mb-4 text-sm text-slate-500">
        Upload at least <strong>2 before</strong> and <strong>2 after</strong> photos (up to 5 each) for every completed job. {data.count} awaiting documentation.
      </p>

      <div className="mb-3 flex flex-wrap gap-2">
        <select value={filters.customer} onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
          data-testid="hpp-filter-customer" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">All customers</option>
          {data.customers.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input placeholder="Branch contains…" value={filters.branch} onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
          data-testid="hpp-filter-branch" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
        <input type="date" value={filters.date} onChange={(e) => setFilters({ ...filters, date: e.target.value })}
          data-testid="hpp-filter-date" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
        <div className="flex items-center gap-1 text-sm text-slate-500"><ArrowUpDown size={14} />
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="hpp-sort"
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
            <option value="oldest">Oldest first</option>
            <option value="newest">Newest first</option>
            <option value="customer">Customer</option>
            <option value="branch">Branch</option>
          </select>
        </div>
      </div>

      {loading ? <div className="p-6 text-slate-400">Loading…</div> : (
        <>
        <div className="hidden lg:block overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="p-2">WO #</th><th className="p-2">Service date</th><th className="p-2">Customer</th>
                <th className="p-2">Branch</th><th className="p-2">Work performed</th>
                <th className="p-2">Before</th><th className="p-2">After</th><th className="p-2">Docs</th><th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 align-top" data-testid={`hpp-row-${r.id}`}>
                  <td className="p-2 font-mono text-xs text-[#0E2A47]">{r.tfs_work_order_number}</td>
                  <td className="p-2 whitespace-nowrap">{fmtRange(r.service_date, r.service_date_end)}</td>
                  <td className="p-2">{r.customer_name}</td>
                  <td className="p-2">{r.service_location}</td>
                  <td className="p-2 max-w-xs text-slate-600">{r.description_of_work}</td>
                  <td className="p-2 text-center whitespace-nowrap" data-testid={`hpp-before-${r.id}`}>
                    <span className={r.before_met ? 'text-green-600 font-semibold' : 'text-amber-600'}>{r.before_photos} of {r.photos_required_each} required</span>
                  </td>
                  <td className="p-2 text-center whitespace-nowrap" data-testid={`hpp-after-${r.id}`}>
                    <span className={r.after_met ? 'text-green-600 font-semibold' : 'text-amber-600'}>{r.after_photos} of {r.photos_required_each} required</span>
                  </td>
                  <td className="p-2 text-xs whitespace-nowrap">
                    {r.documentation_status}
                    {r.exception_rejection_reason && (
                      <div className="mt-0.5 text-[11px] text-red-600" data-testid={`hpp-rejected-${r.id}`}>
                        Exception rejected: {r.exception_rejection_reason}
                      </div>
                    )}
                  </td>
                  <td className="p-2 whitespace-nowrap">
                    <div className="flex gap-1">
                      <button onClick={() => onOpenWorkOrder && onOpenWorkOrder(r.id)} data-testid={`hpp-open-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-[#0E2A47] px-2 py-1 text-xs font-semibold text-white hover:bg-[#16324F]">
                        <ExternalLink size={12} /> Open Work Order
                      </button>
                      <button onClick={() => { setExc(r); setReason(''); }} data-testid={`hpp-exception-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-amber-300 px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50">
                        <AlertTriangle size={12} /> Photos unavailable
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={9} className="p-6 text-center text-slate-400"><Camera size={20} className="mx-auto mb-1" />No jobs are awaiting photos.</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Mobile / tablet: compact photo-pending cards with actions */}
        <MobileRecordList className="lg:hidden" testid="hpp-mobile-list">
          {rows.map((r) => (
            <MobileRecordCard
              key={r.id}
              testid={`hpp-card-${r.id}`}
              title={r.tfs_work_order_number}
              badge={<span className="whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{r.documentation_status}</span>}
              meta={[
                { label: 'Service date', value: fmtRange(r.service_date, r.service_date_end) },
                { label: 'Customer', value: r.customer_name },
                { label: 'Branch', value: r.service_location, full: true },
                { label: 'Work performed', value: r.description_of_work, full: true },
                { label: 'Before', value: <span className={r.before_met ? 'text-green-600 font-semibold' : 'text-amber-600'}>{r.before_photos} of {r.photos_required_each}</span> },
                { label: 'After', value: <span className={r.after_met ? 'text-green-600 font-semibold' : 'text-amber-600'}>{r.after_photos} of {r.photos_required_each}</span> },
              ]}
            >
              {r.exception_rejection_reason && (
                <div className="mt-1.5 text-[11px] text-red-600" data-testid={`hpp-rejected-m-${r.id}`}>Exception rejected: {r.exception_rejection_reason}</div>
              )}
              <div className="mt-2 flex flex-wrap gap-2 border-t border-slate-100 pt-2">
                <button onClick={() => onOpenWorkOrder && onOpenWorkOrder(r.id)} data-testid={`hpp-open-m-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-[#0E2A47] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#16324F]">
                  <ExternalLink size={12} /> Open Work Order
                </button>
                <button onClick={() => { setExc(r); setReason(''); }} data-testid={`hpp-exception-m-${r.id}`}
                  className="inline-flex items-center gap-1 rounded-md border border-amber-300 px-2.5 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-50">
                  <AlertTriangle size={12} /> Photos unavailable
                </button>
              </div>
            </MobileRecordCard>
          ))}
          {!rows.length && (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-400">
              <Camera size={20} className="mx-auto mb-1" />No jobs are awaiting photos.
            </div>
          )}
        </MobileRecordList>
        </>
      )}

      {data.pending_exceptions?.length > 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="hpp-pending-exceptions">
          <div className="mb-2 text-sm font-bold text-amber-800">Exceptions awaiting Global Administrator approval ({data.pending_exceptions.length})</div>
          <ul className="space-y-1 text-sm">
            {data.pending_exceptions.map((r) => (
              <li key={r.id} className="rounded-md border border-amber-200 bg-white px-3 py-2" data-testid={`hpp-pending-exc-${r.id}`}>
                <span className="font-mono text-xs text-[#0E2A47]">{r.tfs_work_order_number}</span> · {r.service_location} · <span className="capitalize">{r.exception_category}</span> unavailable — <span className="text-slate-500">{r.exception_reason}</span>
                <span className="ml-1 text-xs font-semibold text-amber-700">Pending approval</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {exc && (
        <AlertDialog open onOpenChange={(o) => !o && setExc(null)}>
          <AlertDialogContent data-testid="hpp-exception-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Photos unavailable for {exc.tfs_work_order_number}?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-slate-600">
                  <p>This does <strong>not</strong> mark photos as uploaded. It submits a request to the Global Administrator for approval, recording your reason.</p>
                  <div>
                    <div className="mb-1 text-xs font-semibold text-slate-600">Which photos are unavailable?</div>
                    <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="hpp-exception-category"
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                      <option value="both">Both before and after</option>
                      <option value="before">Before photos only</option>
                      <option value="after">After photos only</option>
                    </select>
                  </div>
                  <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="hpp-exception-reason"
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" rows={3}
                    placeholder="Why are the photos unavailable? (e.g. not captured on site during emergency response)" />
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="hpp-exception-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); submitException(); }}
                className="bg-amber-600 hover:bg-amber-700" data-testid="hpp-exception-confirm">
                {busy ? 'Submitting…' : 'Submit for Approval'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default HistoricalPhotosPendingView;
